import React from 'react';
import styled, { keyframes } from 'styled-components';

import useTranslation from 'hooks/use-translation';

import PinIcon from 'assets/marketing/pin-icon';
import { Button } from 'components/core';
import { MAIN_CONTENT_ID } from 'components/skip-to-main/constants';
import { ARIA_ROLES } from 'shared/constants';
import { DesktopOnly, MobileOnly } from 'shared/components';

import { DesktopAddressComboboxDarkController } from './desktop-address-combobox-dark.controller';
import { palette, serifItalic } from './palette';
import { PillTag } from './primitives';

type RedesignHeroProps = {
  onOpenAddressDialog: () => void;
};

export function RedesignHero({ onOpenAddressDialog }: RedesignHeroProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <HeroSection data-ga-label='hero' role={ARIA_ROLES.BANNER}>
      <HeroInner role={ARIA_ROLES.REGION} id={MAIN_CONTENT_ID}>
        <h1>
          {t('home.redesign.heroTitle', 'Find your local')}
          <em>{t('home.redesign.heroTitleAccent', 'dispensary.')}</em>
        </h1>
        <Lede>
          {t(
            'home.redesign.heroLede',
            'Browse real-time menus, order ahead for pickup, or get delivery—directly from dispensaries near you.'
          )}
        </Lede>

        <DesktopOnly display='block' mediaQuery='sm'>
          <SearchWrap>
            <DesktopAddressComboboxDarkController />
          </SearchWrap>
        </DesktopOnly>

        <MobileOnly display='block' mediaQuery='xs'>
          <MobileAddressButton
            onClick={onOpenAddressDialog}
            data-cy='home-hero-address-input-mobile'
            data-test='home-hero-address-input-mobile'
            data-testid='homeAddressInput'
          >
            <StyledPinIcon height='23px' />
            {t('home.hero.mobileAddressPlaceholder', 'Enter an address or city')}
          </MobileAddressButton>
        </MobileOnly>

        <HeroTags>
          <PillTag>{t('home.redesign.pickupNearby', 'Pickup nearby')}</PillTag>
          <PillTag>{t('home.redesign.delivery', 'Delivery')}</PillTag>
          <PillTag>{t('home.redesign.payByBank', 'Pay by Bank')}</PillTag>
        </HeroTags>

        <SubNote>
          {t('home.redesign.heroSubNote', 'Connecting you to 6,500+ dispensaries across North America')}
        </SubNote>
      </HeroInner>
      <ScrollHint aria-hidden='true'>
        <svg
          width='22'
          height='22'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <polyline points='6 9 12 15 18 9' />
        </svg>
      </ScrollHint>
    </HeroSection>
  );
}

const scrollBounce = keyframes`
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50%      { transform: translateX(-50%) translateY(6px); }
`;

const HeroSection = styled.div`
  position: relative;
  padding: 88px 24px;
  min-height: 75vh;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  background: radial-gradient(ellipse 80% 80% at 50% 50%, ${palette.bg} 0%, ${palette.bgDeep} 100%);

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    min-height: 0;
    padding: 60px 20px;
  }
`;

const HeroInner = styled.div`
  text-align: center;
  position: relative;
  z-index: 5;
  max-width: 900px;
  margin: 0 auto;
  width: 100%;

  h1 {
    font-size: clamp(36px, 5vw, 72px);
    line-height: 1;
    margin: 0 0 28px;
    font-weight: 700;
    color: ${palette.cream};
    letter-spacing: -1px;
  }

  h1 em {
    display: block;
    font-style: italic;
    color: ${palette.gold};
    font-weight: 700;
    font-family: ${serifItalic};
  }
`;

const Lede = styled.p`
  max-width: 580px;
  margin: 0 auto 32px;
  font-size: 17px;
  color: ${palette.creamDim};
  line-height: 1.5;
`;

const SearchWrap = styled.div`
  width: 100%;
  max-width: 696px;
  margin: 0 auto;
`;

const StyledPinIcon = styled(PinIcon)`
  margin-right: 8px;
  fill: ${palette.goldFirst};
`;

const MobileAddressButton = styled(Button)`
  border-radius: 12px;
  margin-top: 8px;
  height: 60px;
  width: 100%;
  text-transform: none;
  font-weight: normal;
  font-size: 16px;
  display: flex;
  align-items: center;
  color: ${palette.inputPlaceholder};
  background-color: ${palette.inputBg};
  border: 1px solid ${palette.inputBorder};

  svg {
    margin-right: 6px;
    fill: ${palette.goldFirst};
  }

  &:hover {
    background-color: ${palette.inputBg};
  }
`;

const HeroTags = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  flex-wrap: wrap;

  span {
    background: rgba(255, 248, 233, 0.05);
    border-color: rgba(255, 192, 42, 0.2);
    color: #f1e8d6;
  }
`;

const SubNote = styled.p`
  margin-top: 20px;
  font-size: 14px;
  color: ${palette.creamDim};
`;

const ScrollHint = styled.div`
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  color: ${palette.gold};
  opacity: 0.6;
  animation: ${scrollBounce} 1.8s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
